"use client";

import { Core } from "@faire/design-tokens";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Row } from "@faire/web--source/slate/Layout";
import {
  Typography,
  TypographyTags,
} from "@faire/web--source/slate/Typography";
import { useStateWithDebounce } from "@faire/web--source/ui/hooks/useStateWithDebounce";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import isUndefined from "lodash/isUndefined";
import * as React from "react";
import { useState } from "react";
import styled, { css, DefaultTheme } from "styled-components";

import { getWindow } from "../../common/globals/getWindow";
import { Badge } from "../Badge";
import { BadgeVariant, StatusBadgeProps } from "../Badge/types";
import { Color } from "../Color";
import { Notification } from "../icons/foundation/Notification";
import { Spacer } from "../spacing";
import { responsiveMediaQueries } from "../Theme/responsiveMediaQueries";
import {
  HIGH_VIS_FILTER,
  highVisSlateComponent,
} from "../utils/highVisSlateComponent";

export type TabAction = {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
};

export type Tab<T extends string = string> = {
  /** Tab's values when it is active. */
  value: T;
  /** Label to display on Tab. */
  label: string;
  /** Numeric value to display in the Pill. */
  amount?: number;
  /** Whether the Pill should display if amount is zero. */
  showZeroAmount?: boolean;
  /**
   * @deprecated use the `badgeBackgroundColour` prop instead
   */
  pillColour?: string;
  /** Optional wrapper to wrap each Tab with. If undefined, will just render each Tab as normal. */
  customTabWrapper?:
    | React.ComponentClass<React.PropsWithChildren<unknown>>
    | React.FC<React.PropsWithChildren<unknown>>;
  /** Optional prop to show a notification dot on the top-right corner */
  notificationDot?: boolean;
  /** Colour to set Badge background to; Fallbacks to default colour. */
  badgeBackgroundColour?: Color;
  /** Colour to set Badge content to; Fallbacks to default colour. */
  badgeContentColour?: Color;
  /** Makes the tab a clickable link while waiting for SSR hydration. */
  ssrLinkTo?: string;
  /** Optional status badge to display on the tab. */
  statusBadge?: StatusBadgeProps;
};

interface IProps<T extends string = string> {
  /** Array of tab values. */
  tabs: Tab<T>[];
  /** Callback handler called when a tab is clicked. Set the active tab in this handler. */
  onTabChange: (newTab: T) => void;
  /** The currently active tab, shown by a black highlight underneath the tab's label. */
  activeTab: T;
  className?: string;
  /** Array of actions with a label, onClick, and optional icon that add actions to the right of the tabs. */
  actions?: TabAction[];
  theme?: DefaultTheme;
  autoScrollIntoView?: boolean;

  /** For emitting events when tabs is horizontally scrolled. */
  onScroll?: (start: number, end: number) => void;

  "data-test-id"?: string;

  /** Specifies the 'as' property for the Label of each Tab. */
  tabAs?: TypographyTags;

  /** Center aligns the tabs */
  centerAlign?: boolean;
}

/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/tabs)
 */
export const Tabs = <T extends string = string>(props: IProps<T>) => {
  const {
    className,
    activeTab,
    actions,
    tabs,
    "data-test-id": dataTestId,
    onScroll,
    centerAlign,
  } = props;
  const isSSR = useIsSSR();
  const tabsRef = React.useRef<HTMLDivElement>(null);

  const [isTabsInView, setIsTabsInView] = React.useState<boolean>(false);

  const [currentIndex, setCurrentIndex] = useState<number>(
    tabs.findIndex((tab) => tab.value === activeTab)
  );

  const observer = React.useMemo(
    () =>
      !isSSR
        ? new IntersectionObserver(([entry]) =>
            setIsTabsInView(entry?.isIntersecting ?? false)
          )
        : undefined,
    [isSSR]
  );

  React.useEffect(() => {
    if (tabsRef.current) {
      observer?.observe(tabsRef.current);
    }
    return () => observer?.disconnect();
  }, [observer]);

  // This set of refs is for managing the focus states
  const tabFocusRefs = React.useMemo(() => {
    return props.tabs.reduce((acc, tab) => {
      acc[tab.value] = React.createRef();
      return acc;
    }, {} as Record<string, React.RefObject<HTMLButtonElement | null>>);
  }, [props.tabs]);

  // This set of refs is for managing the scrolling behavior
  // It's placed on the wrapper, making sure the scroll maintains the padding
  const tabScrollRefs = React.useMemo(() => {
    return props.tabs.reduce((acc, tab) => {
      acc[tab.value] = React.createRef();
      return acc;
    }, {} as Record<string, React.RefObject<HTMLDivElement | null>>);
  }, [props.tabs]);

  React.useEffect(() => {
    const newIndex = tabs.findIndex((tab) => tab.value === activeTab);
    setCurrentIndex(newIndex);
  }, [activeTab, tabs]);

  const scrollToTab = React.useCallback(
    (tabScrollRef?: React.RefObject<HTMLDivElement | null>) => {
      if (props.autoScrollIntoView && tabScrollRef && isTabsInView) {
        tabScrollRef.current?.scrollIntoView({
          inline: "nearest",
          block: "nearest",
        });
      }
    },
    [isTabsInView, props.autoScrollIntoView]
  );

  // We want to use layout effect here to make sure the view is painted
  // before the tab is scrolled into view, and subsequently scroll everytime
  // the active tab changes
  React.useLayoutEffect(() => {
    scrollToTab(tabScrollRefs[props.activeTab]);
  }, [props.activeTab, tabScrollRefs, scrollToTab]);

  const setActiveTab = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const tabLabel = event.currentTarget.firstChild?.textContent;
    const newTab = props.tabs.find((tab) => tab.label === tabLabel)?.value;
    if (newTab !== undefined) {
      props.onTabChange(newTab);
    }
  };

  const DEBOUNCE_DELAY = 500;

  const {
    setValue: setScrollMeasurements,
    debouncedValue: debouncedScrollMeasurements,
  } = useStateWithDebounce([0, undefined], DEBOUNCE_DELAY);

  const onHorizontalScroll = () => {
    if (!onScroll) {
      return;
    }

    const scrollWidth = tabsRef.current?.scrollWidth || 0 + 2 * PADDING_SIZE; // width of the entire element (including overflow).
    const scrollLeft = tabsRef.current?.scrollLeft;
    const clientWidth = tabsRef.current?.clientWidth || 0 + 2 * PADDING_SIZE; // width not including overflow.

    if (
      isUndefined(scrollWidth) ||
      isUndefined(scrollLeft) ||
      isUndefined(clientWidth)
    ) {
      return;
    }

    const start = scrollLeft / scrollWidth;
    const end = (scrollLeft + clientWidth) / scrollWidth;

    setScrollMeasurements([start, end]);
  };

  React.useEffect(() => {
    if (!onScroll) {
      return;
    }

    const [start, end] = debouncedScrollMeasurements;

    if (!isUndefined(start) && !isUndefined(end)) {
      onScroll(start, end);
    }
    // onScroll dependency keeps re-triggering when setActiveTab is called.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedScrollMeasurements]);

  if (tabs.length === 0) {
    return null;
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    let newTabIndex = -1;

    const lastTab = tabs.length - 1;
    const previousTabWraps = currentIndex === 0 ? lastTab : currentIndex - 1;
    const nextTabWraps = currentIndex === lastTab ? 0 : currentIndex + 1;

    const isRTL = getWindow()?.document.dir === "rtl";

    if (event.key === "ArrowLeft") {
      newTabIndex = isRTL ? nextTabWraps : previousTabWraps;
    } else if (event.key === "ArrowRight") {
      newTabIndex = isRTL ? previousTabWraps : nextTabWraps;
    } else if (event.key === "Home") {
      newTabIndex = 0;
    } else if (event.key === "End") {
      newTabIndex = lastTab;
    }

    const newTab = props.tabs[newTabIndex]?.value;

    if (newTab !== undefined) {
      props.onTabChange(newTab);
      tabFocusRefs[newTab]?.current?.focus();
    }
  };

  return (
    <TabsWrapper
      className={className}
      onScroll={onHorizontalScroll}
      ref={tabsRef}
      data-test-id={dataTestId}
      onKeyDown={handleKeyDown}
      role="tablist"
    >
      <TabsContainer $centerAlign={centerAlign}>
        {tabs.map((tab) => {
          const CustomTabWrapper = tab.customTabWrapper;
          const tabMarkup = (
            <Tab
              tabFocusRef={tabFocusRefs[tab.value]}
              tabScrollRef={tabScrollRefs[tab.value]}
              key={tab.label}
              active={tab.value === activeTab}
              onClick={setActiveTab}
              tab={tab}
              as={props.tabAs}
            />
          );
          return CustomTabWrapper ? (
            <CustomTabWrapper key={tab.label}>{tabMarkup}</CustomTabWrapper>
          ) : (
            tabMarkup
          );
        })}
      </TabsContainer>
      {actions
        ? actions.map((action) => (
            <ActionWrapper
              onClick={action.onClick}
              key={action.label}
              data-test-id={action.label}
            >
              {action.icon ? (
                <>
                  {action.icon}
                  <Spacer size={4} />
                </>
              ) : null}
              <Typography
                variant="paragraphSansMedium"
                color={Core.text.default}
              >
                {action.label}
              </Typography>
            </ActionWrapper>
          ))
        : null}
    </TabsWrapper>
  );
};

const Tab: React.FC<{
  active: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  tab: Tab<any>;
  tabFocusRef: React.MutableRefObject<HTMLButtonElement | null> | undefined;
  tabScrollRef: React.MutableRefObject<HTMLDivElement | null> | undefined;
  as?: TypographyTags;
}> = ({ active, onClick, tab, tabFocusRef, tabScrollRef, as }) => {
  const {
    amount,
    label,
    showZeroAmount,
    pillColour,
    notificationDot,
    badgeBackgroundColour,
    badgeContentColour,
    statusBadge,
  } = tab;
  const tabTextVariant = active
    ? "paragraphSansMedium"
    : "paragraphSansRegular";
  const showAmount = amount !== undefined && (showZeroAmount || amount !== 0);
  const showNotification = !showAmount && notificationDot;

  return (
    <TabSpacer ref={tabScrollRef}>
      <TabWrapper
        data-test-id={label}
        $active={active}
        aria-selected={active}
        onClick={onClick}
        ref={tabFocusRef}
        role="tab"
        tabIndex={active ? 0 : -1}
      >
        <LinkDuringSSR ssrLinkTo={tab.ssrLinkTo}>
          <Label variant={tabTextVariant} forwardedAs={as ?? "p"}>
            {label}
          </Label>
          {showNotification ? <Notification /> : null}
          {showAmount ? (
            <>
              <Spacer size={4} />
              <Badge
                backgroundColor={badgeBackgroundColour ?? pillColour}
                contentColor={badgeContentColour}
                content={amount}
                variant={BadgeVariant.Notification}
                notificationDot={notificationDot}
              />
            </>
          ) : null}
          {statusBadge ? (
            <>
              <Spacer size={4} />
              <Badge {...statusBadge} />
            </>
          ) : null}
        </LinkDuringSSR>
      </TabWrapper>
    </TabSpacer>
  );
};

const LinkDuringSSR: React.FC<{
  ssrLinkTo: string | undefined;
  children: React.ReactNode;
}> = ({ ssrLinkTo, children }) => {
  const isSSR = useIsSSR();
  if (isSSR && ssrLinkTo) {
    return <Link to={ssrLinkTo}>{children}</Link>;
  } else {
    return children;
  }
};

const Label = styled(Typography)`
  white-space: nowrap;
`;

const TabsContainer = styled(Row)<{ $centerAlign?: boolean }>`
  ${(props) =>
    props.$centerAlign
      ? css`
          margin: 0 auto;
        `
      : ""}
`;

const TabsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  height: 52px;
  border-bottom: 1px solid ${Core.border.muted};
  overflow-y: auto;
  ${responsiveMediaQueries.tabletAndBelow} {
    scrollbar-width: none;
    -ms-overflow-style: none;
    ::-webkit-scrollbar {
      width: 0;
      height: 0;
    }
  }
`;

const ActionWrapper = styled.button`
  display: flex;
  align-items: center;
  margin: 0 12px;
  height: 100%;
  cursor: pointer;

  &:focus-visible {
    outline: 5px auto Highlight;
    outline: 5px auto -webkit-focus-ring-color;
  }
`;

const TabWrapper = styled.button<{
  $active: boolean;
}>`
  display: flex;
  align-items: center;
  height: 100%;
  cursor: pointer;
  border-bottom: ${(props) =>
    props.$active ? css`1px solid ${Core.border.subdued}` : "unset"};
  ${highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT)}

  &:focus-visible {
    outline: 5px auto Highlight;
    outline: 5px auto -webkit-focus-ring-color;
  }
`;

const PADDING_SIZE = 24;

const TabSpacer = styled.div`
  height: 100%;
  padding: 0 ${PADDING_SIZE}px;
`;
